import { useState } from 'react'
import {
  Title, Group, Card, Stack, Text, TextInput, Textarea, Button,
  Table, Badge, ActionIcon, Modal, LoadingOverlay, Select, Switch,
} from '@mantine/core'
import {
  IconPlus, IconPencil, IconTrash, IconPhoto,
  IconEye, IconEyeOff,
} from '@tabler/icons-react'
import { useForm, Controller } from 'react-hook-form'
import toast from 'react-hot-toast'
import { api } from '@/lib/api'
import { confirmDelete } from '@/lib/confirm'
import { useNews, useDoctors } from '@/lib/swr'
import type { News, NewsCategory, Doctor } from '@/types'

interface NewsFormData {
  title: string
  slug: string
  category: NewsCategory
  excerpt: string
  content: string
  image: string
  author: string
  doctor: string
  color: string
  featured: boolean
}

const EMPTY: NewsFormData = {
  title: '', slug: '', category: 'news', excerpt: '', content: '',
  image: '', author: 'NatureMed', doctor: '', color: '#00897B', featured: false,
}

const CATEGORY_OPTIONS = [
  { value: 'news', label: 'Мэдээ' },
  { value: 'recommendation', label: 'Зөвлөгөө' },
  { value: 'faq', label: 'Түгээмэл асуулт хариулт' },
]

const categoryLabel = (c: NewsCategory) =>
  CATEGORY_OPTIONS.find((o) => o.value === c)?.label ?? c

export default function NewsList() {
  const { data, isLoading, mutate } = useNews({ limit: '100' })
  const items: News[] = data?.data ?? []
  const { data: doctorsData } = useDoctors({ limit: '200' })
  const allDoctors: Doctor[] = doctorsData?.data ?? []
  const doctorOptions = allDoctors.map((d) => ({ value: d._id, label: d.name }))
  const getDoctorName = (id: string) => allDoctors.find((d) => d._id === id)?.name ?? ''

  const [modalOpen, setModalOpen] = useState(false)
  const [editId, setEditId] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<NewsFormData>({ defaultValues: EMPTY })

  const watchImage = watch('image')

  const openCreate = () => {
    setEditId(null)
    reset(EMPTY)
    setModalOpen(true)
  }

  const openEdit = (item: News) => {
    setEditId(item._id)
    reset({
      title: item.title,
      slug: item.slug || '',
      category: item.category,
      excerpt: item.excerpt || '',
      content: item.content || '',
      image: item.image || '',
      author: item.author || 'NatureMed',
      doctor: (item as any).doctor || '',
      color: item.color || '#00897B',
      featured: item.featured || false,
    })
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    reset(EMPTY)
  }

  const onSubmit = handleSubmit(async (form) => {
    try {
      const payload = { ...form, doctor: form.doctor || null } as unknown as Record<string, unknown>
      if (editId) {
        await api.admin.updateNews(editId, payload)
      } else {
        await api.admin.createNews(payload)
      }
      toast.success(editId ? 'Мэдээ шинэчлэгдлээ' : 'Мэдээ нэмэгдлээ')
      closeModal()
      mutate()
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    }
  })

  const handleDelete = (id: string) => confirmDelete('Энэ мэдээг устгах уу?', async () => {
    try {
      await api.admin.deleteNews(id)
      toast.success('Мэдээ устгагдлаа')
      mutate()
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    }
  })

  const toggleStatus = async (item: News) => {
    try {
      await api.admin.updateNewsStatus(item._id, !item.isActive)
      mutate()
    } catch (err: any) {
      toast.error(err instanceof Error ? err.message : 'Алдаа гарлаа')
    }
  }

  const handleImageUpload = async (file: File) => {
    setUploading(true)
    try {
      const result = await api.admin.uploadImage(file)
      setValue('image', result.url, { shouldDirty: true })
      toast.success('Зураг байршуулагдлаа')
    } catch {
      toast.error('Зураг байршуулахад алдаа гарлаа')
    } finally {
      setUploading(false)
    }
  }

  return (
    <Stack>
      <Group justify="space-between">
        <Title order={2}>Мэдээ, нийтлэл</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={openCreate}>Мэдээ нэмэх</Button>
      </Group>

      <Card withBorder pos="relative">
        <LoadingOverlay visible={isLoading} />
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Зураг</Table.Th>
              <Table.Th>Гарчиг</Table.Th>
              <Table.Th>Ангилал</Table.Th>
              <Table.Th>Зохиогч</Table.Th>
              <Table.Th>Огноо</Table.Th>
              <Table.Th>Статус</Table.Th>
              <Table.Th w={140}>Үйлдэл</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((item) => (
              <Table.Tr key={item._id}>
                <Table.Td>
                  {item.image ? (
                    <img src={item.image} alt="" style={{ width: 48, height: 36, borderRadius: 4, objectFit: 'cover' }} />
                  ) : (
                    <div style={{ width: 48, height: 36, borderRadius: 4, background: item.color || '#00897B' }} />
                  )}
                </Table.Td>
                <Table.Td>
                  <Text size="sm" fw={600} lineClamp={1}>{item.title}</Text>
                  {item.featured && <Badge size="xs" color="orange" variant="light">Онцлох</Badge>}
                </Table.Td>
                <Table.Td>
                  <Badge variant="light" size="sm">{categoryLabel(item.category)}</Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="xs">{item.author}</Text>
                  {(item as any).doctor && <Text size="xs" c="teal">{getDoctorName((item as any).doctor)}</Text>}
                </Table.Td>
                <Table.Td><Text size="xs">{item.publishedAt ? new Date(item.publishedAt).toLocaleDateString('mn-MN') : '—'}</Text></Table.Td>
                <Table.Td>
                  <Badge color={item.isActive ? 'green' : 'gray'} variant="light" size="sm">
                    {item.isActive ? 'Идэвхтэй' : 'Идэвхгүй'}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Group gap={4}>
                    <ActionIcon variant="light" size="sm" onClick={() => toggleStatus(item)}>
                      {item.isActive ? <IconEye size={14} /> : <IconEyeOff size={14} />}
                    </ActionIcon>
                    <ActionIcon variant="light" size="sm" onClick={() => openEdit(item)}>
                      <IconPencil size={14} />
                    </ActionIcon>
                    <ActionIcon variant="light" color="red" size="sm" onClick={() => handleDelete(item._id)}>
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Group>
                </Table.Td>
              </Table.Tr>
            ))}
            {items.length === 0 && !isLoading && (
              <Table.Tr><Table.Td colSpan={7}><Text ta="center" c="dimmed" py="lg">Мэдээ бүртгэгдээгүй байна</Text></Table.Td></Table.Tr>
            )}
          </Table.Tbody>
        </Table>
      </Card>

      <Modal opened={modalOpen} onClose={closeModal} title={editId ? 'Мэдээ засах' : 'Шинэ мэдээ'} size="lg">
        <form onSubmit={onSubmit}>
          <Stack>
            <Controller
              name="title"
              control={control}
              rules={{ validate: (v) => (v.trim() ? true : 'Гарчиг шаардлагатай') }}
              render={({ field }) => <TextInput label="Гарчиг *" {...field} error={errors.title?.message} />}
            />
            <Group grow>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <Select label="Ангилал *" data={CATEGORY_OPTIONS} value={field.value} onChange={(v) => field.onChange(v as NewsCategory)} />
                )}
              />
              <Controller
                name="author"
                control={control}
                render={({ field }) => <TextInput label="Зохиогч" {...field} />}
              />
            </Group>
            <Controller
              name="doctor"
              control={control}
              render={({ field }) => (
                <Select
                  label="Холбоотой эмч"
                  placeholder="Эмч сонгох (заавал биш)"
                  data={doctorOptions}
                  value={field.value || null}
                  onChange={(v) => field.onChange(v || '')}
                  searchable
                  clearable
                />
              )}
            />
            <Group grow>
              <Controller
                name="slug"
                control={control}
                render={({ field }) => <TextInput label="Slug" placeholder="URL-д ашиглах" {...field} />}
              />
              <Controller
                name="color"
                control={control}
                render={({ field }) => <TextInput label="Өнгө" placeholder="#00897B" {...field} />}
              />
            </Group>
            <Controller
              name="excerpt"
              control={control}
              render={({ field }) => <Textarea label="Товч тайлбар" {...field} minRows={2} />}
            />
            <Controller
              name="content"
              control={control}
              render={({ field }) => <Textarea label="Контент" {...field} minRows={6} autosize maxRows={20} />}
            />

            <div>
              <Text size="sm" fw={500} mb={4}>Зураг</Text>
              <Group>
                <Controller
                  name="image"
                  control={control}
                  render={({ field }) => <TextInput flex={1} placeholder="URL" {...field} />}
                />
                <Button
                  size="sm" variant="light" leftSection={<IconPhoto size={14} />}
                  loading={uploading}
                  onClick={() => {
                    const input = document.createElement('input')
                    input.type = 'file'
                    input.accept = 'image/*'
                    input.onchange = () => input.files?.[0] && handleImageUpload(input.files[0])
                    input.click()
                  }}
                >
                  Upload
                </Button>
              </Group>
              {watchImage && <img src={watchImage} alt="" style={{ marginTop: 8, maxHeight: 120, borderRadius: 8, objectFit: 'cover' }} />}
            </div>

            <Controller
              name="featured"
              control={control}
              render={({ field }) => (
                <Switch label="Онцлох" checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
              )}
            />

            <Group justify="flex-end">
              <Button variant="light" onClick={closeModal} disabled={isSubmitting}>Болих</Button>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>{editId ? 'Хадгалах' : 'Нэмэх'}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
  )
}
